import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import {
  eachDayOfInterval,
  eachMonthOfInterval,
  endOfDay,
  endOfMonth,
  format,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import { useHotel } from "@/hooks/useHotel";
import type { PeriodType } from "@/components/pages/dashboard/components/charts/dashboard_charts/periodType";
import type { Feedback } from "@/services/interfaces/IFeedbackService";
import { feedbacksService } from "@/services/feedbacks-service";
import { roomService } from "@/services/roomService";
import { requestService } from "@/services/requestService";
import { RequestType, type Request } from "@/types/request";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import type { RoomUsageAnalyticsSeriesPoint } from "@/types/roomUsage";

const CHART_FEEDBACK_LIMIT = 2000;
const CHART_REQUESTS_LIMIT = 2000;

function toCreatedAtDate(value: Feedback["createdAt"]): Date | null {
  const d = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(d.getTime()) ? null : d;
}

function bucketKeyForPeriod(periodType: PeriodType, d: Date): string {
  if (periodType === "1y") {
    return format(startOfMonth(d), "yyyy-MM");
  }
  return format(startOfDay(d), "yyyy-MM-dd");
}

function buildTimeline(periodType: PeriodType, now: Date) {
  if (periodType === "7d") {
    const start = startOfDay(subDays(now, 6));
    const end = endOfDay(now);
    const days = eachDayOfInterval({ start, end });
    return {
      bucketKeys: days.map((d) => format(d, "yyyy-MM-dd")),
      categories: days.map((d) => format(d, "MMM d")),
      rangeStart: start,
      rangeEnd: end,
    };
  }
  if (periodType === "14d") {
    const start = startOfDay(subDays(now, 13));
    const end = endOfDay(now);
    const days = eachDayOfInterval({ start, end });
    return {
      bucketKeys: days.map((d) => format(d, "yyyy-MM-dd")),
      categories: days.map((d) => format(d, "MMM d")),
      rangeStart: start,
      rangeEnd: end,
    };
  }
  if (periodType === "1m") {
    const start = startOfDay(subDays(now, 29));
    const end = endOfDay(now);
    const days = eachDayOfInterval({ start, end });
    return {
      bucketKeys: days.map((d) => format(d, "yyyy-MM-dd")),
      categories: days.map((d) => format(d, "MMM d")),
      rangeStart: start,
      rangeEnd: end,
    };
  }
  const start = startOfMonth(subMonths(now, 11));
  const end = endOfMonth(now);
  const months = eachMonthOfInterval({ start, end });
  return {
    bucketKeys: months.map((m) => format(m, "yyyy-MM")),
    categories: months.map((m) => format(m, "MMM yyyy")),
    rangeStart: start,
    rangeEnd: end,
  };
}

function aggregateUpsellsByBucket(
  requests: Request[],
  periodType: PeriodType,
  bucketKeys: string[],
): number[] {
  const counts = new Map<string, number>();
  for (const key of bucketKeys) {
    counts.set(key, 0);
  }
  for (const req of requests) {
    if (!req.createdAt) continue;
    const d = new Date(req.createdAt);
    if (Number.isNaN(d.getTime())) continue;
    const key = bucketKeyForPeriod(periodType, d);
    const prev = counts.get(key);
    if (prev !== undefined) {
      counts.set(key, prev + 1);
    }
  }
  return bucketKeys.map((key) => counts.get(key) ?? 0);
}

function aggregateGuestSatisfactionByBucket(
  items: Feedback[],
  periodType: PeriodType,
  bucketKeys: string[],
): number[] {
  const sums = new Map<string, { sum: number; count: number }>();
  for (const key of bucketKeys) {
    sums.set(key, { sum: 0, count: 0 });
  }
  for (const item of items) {
    const d = toCreatedAtDate(item.createdAt);
    if (!d) continue;
    const key = bucketKeyForPeriod(periodType, d);
    const agg = sums.get(key);
    if (!agg) continue;
    if (typeof item.rating === "number" && Number.isFinite(item.rating)) {
      agg.sum += item.rating;
      agg.count += 1;
    }
  }
  return bucketKeys.map((key) => {
    const agg = sums.get(key);
    if (!agg || agg.count === 0) {
      return -1;
    }
    return agg.sum / agg.count;
  });
}

function aggregateOccupancyByBucket(
  series: RoomUsageAnalyticsSeriesPoint[] | undefined,
  bucketKeys: string[],
): number[] {
  const occupancyByBucket = new Map<string, number>();

  for (const point of series ?? []) {
    if (!point.bucket || typeof point.occupancyRate !== "number") continue;

    occupancyByBucket.set(
      point.bucket,
      Math.min(100, Math.round(point.occupancyRate * 100)),
    );
  }

  return bucketKeys.map((key) => occupancyByBucket.get(key) ?? 0);
}

export const useDashboardAnalyticsChartData = (periodType: PeriodType) => {
  const { hotelId } = useHotel();
  const { capabilities, hasFeedbacksAccess } = useAccessCapabilities();
  const enabled = Boolean(hotelId);
  const feedbacksEnabled = enabled && hasFeedbacksAccess;
  const requestFeedbacksEnabled =
    feedbacksEnabled && capabilities.canUseRequestsSystem;
  const upsellsEnabled = enabled && capabilities.canUseRequestsSystem;

  const timeline = useMemo(
    () => buildTimeline(periodType, new Date()),
    [periodType],
  );

  const rangeStartIso = timeline.rangeStart.toISOString();
  const rangeEndIso = timeline.rangeEnd.toISOString();
  const occupancyBucket = periodType === "1y" ? "month" : "day";

  const queries = useQueries({
    queries: [
      {
        queryKey: [
          "dashboard",
          "analyticsChart",
          "occupancy",
          hotelId,
          periodType,
          rangeStartIso,
          rangeEndIso,
          occupancyBucket,
        ] as const,
        queryFn: () => {
          const id = hotelId;
          if (!id) {
            return Promise.reject(new Error("Missing hotel"));
          }
          return roomService.getRoomUsageAnalytics(id, {
            from: rangeStartIso,
            to: rangeEndIso,
            groupBy: "category",
            bucket: occupancyBucket,
          });
        },
        enabled,
        staleTime: 120_000,
      },
      {
        queryKey: [
          "dashboard",
          "analyticsChart",
          "requests",
          hotelId,
          periodType,
          rangeStartIso,
          rangeEndIso,
        ] as const,
        queryFn: () => {
          const id = hotelId;
          if (!id) {
            return Promise.reject(new Error("Missing hotel"));
          }
          return feedbacksService.getAllRequestFeedbacks({
            hotelId: id,
            page: 1,
            limit: CHART_FEEDBACK_LIMIT,
            feedbackStartDateRange: rangeStartIso,
            feedbackEndDateRange: rangeEndIso,
          });
        },
        enabled: requestFeedbacksEnabled,
        staleTime: 120_000,
      },
      {
        queryKey: [
          "dashboard",
          "analyticsChart",
          "checkouts",
          hotelId,
          periodType,
          rangeStartIso,
          rangeEndIso,
        ] as const,
        queryFn: () => {
          const id = hotelId;
          if (!id) {
            return Promise.reject(new Error("Missing hotel"));
          }
          return feedbacksService.getAllCheckoutFeedbacks({
            hotelId: id,
            page: 1,
            limit: CHART_FEEDBACK_LIMIT,
            feedbackStartDateRange: rangeStartIso,
            feedbackEndDateRange: rangeEndIso,
          });
        },
        enabled: feedbacksEnabled,
        staleTime: 120_000,
      },
      {
        queryKey: [
          "dashboard",
          "analyticsChart",
          "communications",
          hotelId,
          periodType,
          rangeStartIso,
          rangeEndIso,
        ] as const,
        queryFn: () => {
          const id = hotelId;
          if (!id) {
            return Promise.reject(new Error("Missing hotel"));
          }
          return feedbacksService.getAllCommunicationFeedbacks({
            hotelId: id,
            page: 1,
            limit: CHART_FEEDBACK_LIMIT,
            feedbackStartDateRange: rangeStartIso,
            feedbackEndDateRange: rangeEndIso,
          });
        },
        enabled: feedbacksEnabled,
        staleTime: 120_000,
      },
      {
        queryKey: [
          "dashboard",
          "analyticsChart",
          "upsells",
          hotelId,
          periodType,
          rangeStartIso,
          rangeEndIso,
        ] as const,
        queryFn: () => {
          const id = hotelId;
          if (!id) {
            return Promise.reject(new Error("Missing hotel"));
          }
          return requestService.getRequests({
            type: RequestType.GUEST,
            hotelId: id,
            startDate: rangeStartIso,
            endDate: rangeEndIso,
            limit: CHART_REQUESTS_LIMIT,
          });
        },
        enabled: upsellsEnabled,
        staleTime: 120_000,
      },
    ],
  });

  const isLoading = enabled && queries.some((q) => q.isLoading);
  const hasError = queries.some((q) => q.isError);

  const n = timeline.bucketKeys.length;
  const [
    occupancyQuery,
    requestFeedbacksQuery,
    checkoutFeedbacksQuery,
    communicationFeedbacksQuery,
    upsellsQuery,
  ] = queries;

  if (!enabled || isLoading || hasError) {
    return {
      categories: timeline.categories,
      occupancyData: Array.from({ length: n }, () => 0),
      guestSatisfactionData: Array.from({ length: n }, () => -1),
      upsellsData: Array.from({ length: n }, () => 0),
    };
  }

  const occupancyData = aggregateOccupancyByBucket(
    occupancyQuery.data?.series,
    timeline.bucketKeys,
  );

  const combinedFeedback: Feedback[] = hasFeedbacksAccess
    ? [
        ...(capabilities.canUseRequestsSystem
          ? (requestFeedbacksQuery.data?.data ?? [])
          : []),
        ...(checkoutFeedbacksQuery.data?.data ?? []),
        ...(communicationFeedbacksQuery.data?.data ?? []),
      ]
    : [];

  const guestSatisfactionData = aggregateGuestSatisfactionByBucket(
    combinedFeedback,
    periodType,
    timeline.bucketKeys,
  );

  const upsellRequests: Request[] = capabilities.canUseRequestsSystem
    ? (upsellsQuery.data?.data ?? [])
    : [];
  const upsellsData = aggregateUpsellsByBucket(
    upsellRequests,
    periodType,
    timeline.bucketKeys,
  );

  return {
    categories: timeline.categories,
    occupancyData,
    guestSatisfactionData,
    upsellsData,
  };
};
